/**
 * Precision Planning — Variable-Rate Scripting  (mvp2_agrotech_precision)
 *
 * Generates Variable-Rate Application (VRA) prescription maps from confirmed
 * anomaly + treatment records.  For each field with satellite index data
 * the page computes a per-zone application-rate recommendation and lets the
 * manager export an ISOXML-compatible prescription (CSV representation) or a
 * GeoJSON zone file.
 *
 * Data sources (all existing endpoints):
 *  • GET /api/satellite/boundaries          — field list
 *  • GET /api/satellite-imagery/:id/records — latest NDVI per field
 *  • GET /api/anomalies                     — unresolved anomalies per field
 *  • GET /api/missions                      — completed remediation missions
 */

import React, { useState, useEffect, useCallback } from 'react';
import {
  Layers, Download, RefreshCw, Activity,
  AlertTriangle, Droplets, FlaskConical, ZapOff,
  ChevronDown, ChevronUp,
} from 'lucide-react';
import Card from '../../components/common/Card';
import Button from '../../components/common/Button';
import Spinner from '../../components/common/Spinner';
import Alert from '../../components/common/Alert';
import {
  getBoundaries, getAllAnomalies, getBatchedImagery, invalidateCache,
  type CachedField, type CachedAnomaly, type CachedImageryRecord,
} from '../../services/precisionDataCache';
import { useOrgSettings } from '../../hooks/useOrgSettings';

// ─── Types ─────────────────────────────────────────────────────────────────────

interface Field {
  id: string;
  name: string;
  area_hectares?: number;
  metadata?: Record<string, any>;
}

interface ImageryIndex {
  index_type: string;
  mean_value: number;
}

interface LatestIndices {
  NDVI?: number;
  NDRE?: number;
  NDWI?: number;
  LST?: number;
  acquisitionDate?: string;
}

interface AnomalyRecord {
  id: string;
  boundary_id: string;
  severity: string;
  anomaly_type?: string;
  status: string;
  change_percentage?: number | string;
  affected_area_hectares?: number;
}

interface ZoneSpec {
  zoneId: string;
  label: string;
  areaHa: number;
  ndvi: number | null;
  applicationRate: number;      // kg/ha or L/ha
  rationale: string;
  colorClass: string;
}

interface PrescriptionEntry {
  field: Field;
  latestIndices: LatestIndices;
  activeAnomalies: AnomalyRecord[];
  zones: ZoneSpec[];
  recommendedProduct: string;
  unit: string;
  expanded: boolean;
}

// ─── VRA helpers ───────────────────────────────────────────────────────────────

const PRODUCT_DEFAULTS: Record<string, { product: string; unit: string }> = {
  tea:       { product: 'Urea (46-0-0)', unit: 'kg/ha' },
  coffee:    { product: 'NPK 17-17-17',  unit: 'kg/ha' },
  oil_palm:  { product: 'MOP (0-0-60)',  unit: 'kg/ha' },
  kiwi:      { product: 'Calcium Nitrate', unit: 'kg/ha' },
  avocado:   { product: 'NPK 10-30-20',  unit: 'kg/ha' },
  wheat:     { product: 'Urea (46-0-0)', unit: 'kg/ha' },
  rice:      { product: 'NPK 20-20-0',   unit: 'kg/ha' },
  sugarcane: { product: 'Urea (46-0-0)', unit: 'kg/ha' },
  vineyard:  { product: 'NPK 12-12-17',  unit: 'kg/ha' },
  maize:     { product: 'DAP (18-46-0)', unit: 'kg/ha' },
  cotton:    { product: 'NPK 15-15-15',  unit: 'kg/ha' },
  other:     { product: 'Compound NPK',  unit: 'kg/ha' },
};

function getProductDefaults(cropType?: string) {
  if (!cropType) return PRODUCT_DEFAULTS.other;
  const key = cropType.toLowerCase().replace(/\s+/g, '_');
  return PRODUCT_DEFAULTS[key] ?? PRODUCT_DEFAULTS.other;
}

/**
 * Derive 3 application zones from NDVI value:
 *  high stress  (NDVI <0.35) → max rate
 *  medium stress(NDVI 0.35–0.55) → standard rate
 *  healthy      (NDVI >0.55) → reduced rate
 */
function deriveZones(field: Field, ndvi: number | null, areaHa: number): ZoneSpec[] {
  const total = areaHa || 1;
  if (ndvi === null) {
    return [{
      zoneId: 'zone-1',
      label: 'Full Field (no index data)',
      areaHa: total,
      ndvi: null,
      applicationRate: 80,
      rationale: 'No satellite index data available — applying standard rate across full boundary',
      colorClass: 'bg-gray-100 dark:bg-gray-800 text-gray-700 dark:text-gray-300',
    }];
  }
  if (ndvi < 0.35) {
    return [
      { zoneId: 'z1', label: 'High Stress Zone',    areaHa: +(total * 0.4).toFixed(2), ndvi, applicationRate: 120, rationale: 'NDVI < 0.35 — severe vigour deficit, max nutrient input', colorClass: 'bg-red-100 dark:bg-red-900/30 text-red-700 dark:text-red-300' },
      { zoneId: 'z2', label: 'Moderate Stress Zone', areaHa: +(total * 0.35).toFixed(2), ndvi: ndvi + 0.08, applicationRate: 90, rationale: 'Transition buffer zone — elevated rate', colorClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
      { zoneId: 'z3', label: 'Healthy Zone',         areaHa: +(total * 0.25).toFixed(2), ndvi: ndvi + 0.22, applicationRate: 50, rationale: 'Vigorous canopy — reduced input to prevent over-fertilisation', colorClass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300' },
    ];
  }
  if (ndvi < 0.55) {
    return [
      { zoneId: 'z1', label: 'Moderate Stress Zone', areaHa: +(total * 0.3).toFixed(2), ndvi, applicationRate: 90, rationale: 'NDVI 0.35–0.55 — moderate vigour shortfall', colorClass: 'bg-orange-100 dark:bg-orange-900/30 text-orange-700 dark:text-orange-300' },
      { zoneId: 'z2', label: 'Standard Zone',         areaHa: +(total * 0.7).toFixed(2), ndvi: ndvi + 0.1, applicationRate: 70, rationale: 'Near-optimal canopy — standard maintenance rate', colorClass: 'bg-yellow-100 dark:bg-yellow-900/30 text-yellow-700 dark:text-yellow-300' },
    ];
  }
  return [{
    zoneId: 'z1', label: 'Healthy Field', areaHa: total, ndvi, applicationRate: 50,
    rationale: 'NDVI > 0.55 — optimal canopy; apply maintenance dose only',
    colorClass: 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-300',
  }];
}

// ─── Sub-components ────────────────────────────────────────────────────────────

function ZonePill({ zone, onRateChange }: { zone: ZoneSpec; onRateChange: (rate: number) => void }) {
  return (
    <div className={`rounded-lg p-3 ${zone.colorClass}`}>
      <div className="flex items-center justify-between gap-2 mb-1">
        <p className="text-xs font-semibold truncate">{zone.label}</p>
        <div className="flex items-center gap-1.5">
          <input
            type="number"
            min={0}
            max={300}
            step={5}
            value={zone.applicationRate}
            onChange={(e) => onRateChange(Number(e.target.value))}
            onClick={(e) => e.stopPropagation()}
            className="w-16 text-xs font-bold text-right px-1.5 py-0.5 rounded border border-current/30 bg-white/60 dark:bg-black/20 focus:outline-none focus:ring-1 focus:ring-current"
            title="Override application rate (kg/ha)"
          />
          <span className="text-xs font-bold whitespace-nowrap">kg/ha</span>
        </div>
      </div>
      <p className="text-xs opacity-75">{zone.areaHa} ha — {zone.rationale}</p>
    </div>
  );
}

function ndviColour(v: number | null | undefined): string {
  if (v === null || v === undefined) return 'text-gray-400';
  if (v < 0.35) return 'text-red-600 dark:text-red-400';
  if (v < 0.55) return 'text-orange-600 dark:text-orange-400';
  return 'text-green-600 dark:text-green-400';
}

interface PrescriptionCardProps {
  entry: PrescriptionEntry;
  onToggle: () => void;
  onRateChange: (zoneId: string, rate: number) => void;
  onExportCSV: () => void;
  onExportGeoJSON: () => void;
}

function PrescriptionCard({ entry, onToggle, onRateChange, onExportCSV, onExportGeoJSON }: PrescriptionCardProps) {
  const { uom } = useOrgSettings();
  const totalVolume = entry.zones.reduce((s, z) => s + z.applicationRate * z.areaHa, 0);
  const ndvi = entry.latestIndices.NDVI ?? null;

  return (
    <Card>
      <div className="flex items-start gap-4 p-5 pb-3">
        <div className="flex-shrink-0 w-10 h-10 rounded-xl bg-violet-100 dark:bg-violet-900/30 flex items-center justify-center">
          <Layers className="w-5 h-5 text-violet-600 dark:text-violet-400" />
        </div>
        <div className="flex-1 min-w-0">
          <div className="flex items-start justify-between gap-3 flex-wrap">
            <div>
              <p className="text-base font-bold text-gray-900 dark:text-white">{entry.field.name}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">
                {entry.field.area_hectares?.toFixed(1) ?? '—'} {uom.area_unit_label}
                {entry.field.metadata?.crop_type && ` · ${entry.field.metadata.crop_type}`}
                {entry.latestIndices.acquisitionDate && ` · Imagery: ${entry.latestIndices.acquisitionDate}`}
              </p>
            </div>
            <div className="flex items-center gap-2 flex-shrink-0">
              {entry.activeAnomalies.length > 0 && (
                <span className="text-xs font-semibold px-2 py-0.5 rounded bg-amber-100 text-amber-700 dark:bg-amber-900/30 dark:text-amber-300 flex items-center gap-1">
                  <AlertTriangle className="w-3 h-3" />{entry.activeAnomalies.length} anomaly
                </span>
              )}
              <button type="button" onClick={onToggle} className="text-xs text-gray-400 hover:text-gray-600 dark:hover:text-gray-300 flex items-center gap-1">
                {entry.expanded ? <><ChevronUp className="w-4 h-4" />Less</> : <><ChevronDown className="w-4 h-4" />Zones</>}
              </button>
            </div>
          </div>

          {/* Quick stats */}
          <div className="flex items-center gap-5 mt-2 flex-wrap">
            <span className="text-xs text-gray-500">
              Latest NDVI: <span className={`font-bold ${ndviColour(ndvi)}`}>{ndvi !== null ? ndvi.toFixed(3) : 'n/a'}</span>
            </span>
            <span className="text-xs text-gray-500">
              Product: <strong className="text-gray-700 dark:text-gray-300">{entry.recommendedProduct}</strong>
            </span>
            <span className="text-xs text-gray-500">
              Est. total: <strong className="text-gray-700 dark:text-gray-300">{totalVolume.toFixed(0)} kg</strong>
            </span>
            <span className="text-xs text-gray-500">
              Zones: <strong className="text-gray-700 dark:text-gray-300">{entry.zones.length}</strong>
            </span>
          </div>
        </div>
      </div>

      {/* Expanded zones */}
      {entry.expanded && (
        <div className="px-5 pb-4 space-y-2 mt-1">
          <p className="text-xs font-semibold text-gray-400 uppercase tracking-wide mb-2">Application Zones</p>
          <p className="text-xs text-gray-400 mb-2">Edit any rate directly — changes are reflected in the export.</p>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-2">
            {entry.zones.map(z => (
              <ZonePill key={z.zoneId} zone={z} onRateChange={(rate) => onRateChange(z.zoneId, rate)} />
            ))}
          </div>
        </div>
      )}

      {/* Export actions */}
      <div className="px-5 pb-5 pt-3 border-t border-gray-100 dark:border-gray-700 flex items-center gap-3 flex-wrap">
        <Button variant="outline" size="sm" onClick={onExportCSV}>
          <Download className="w-3.5 h-3.5 mr-1.5" />Export ISOXML CSV
        </Button>
        <Button variant="ghost" size="sm" onClick={onExportGeoJSON}>
          <Download className="w-3.5 h-3.5 mr-1.5" />Export GeoJSON
        </Button>
      </div>
    </Card>
  );
}

// ─── CSV export helper ─────────────────────────────────────────────────────────

function buildCsv(entry: PrescriptionEntry): string {
  const lines = [
    '# ISOXML-compatible VRA Prescription',
    `# Field: ${entry.field.name}`,
    `# Product: ${entry.recommendedProduct}  Unit: ${entry.unit}`,
    `# Generated: ${new Date().toISOString()}`,
    '',
    'Zone,Label,Area_ha,NDVI,Rate_kg_per_ha,Rationale',
    ...entry.zones.map(z =>
      `${z.zoneId},"${z.label}",${z.areaHa},${z.ndvi?.toFixed(3) ?? 'n/a'},${z.applicationRate},"${z.rationale}"`
    ),
  ];
  return lines.join('\n');
}

function buildGeoJSON(entry: PrescriptionEntry): string {
  const features = entry.zones.map((z, i) => ({
    type: 'Feature',
    properties: {
      zone_id: z.zoneId,
      label: z.label,
      area_ha: z.areaHa,
      ndvi: z.ndvi,
      application_rate: z.applicationRate,
      unit: entry.unit,
      product: entry.recommendedProduct,
    },
    geometry: null as any,   // geometry would come from sub-zone polygons (not yet available)
  }));
  return JSON.stringify({ type: 'FeatureCollection', features }, null, 2);
}

function downloadText(content: string, filename: string) {
  const blob = new Blob([content], { type: 'text/plain' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

// ─── Page ──────────────────────────────────────────────────────────────────────

export const VariableRateScriptingPage: React.FC = () => {
  const { uom } = useOrgSettings();
  const [prescriptions, setPrescriptions] = useState<PrescriptionEntry[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  const load = useCallback(async (bust = false) => {
    try {
      setLoading(true);
      setError(null);
      if (bust) invalidateCache();

      // All three base requests share a module-level TTL cache across pages
      const [fields, allAnomalies] = await Promise.all([
        getBoundaries(),
        getAllAnomalies().catch(() => [] as CachedAnomaly[]),
      ]);

      // Group anomalies by boundary client-side — zero extra requests
      const anomalyMap = new Map<string, CachedAnomaly[]>();
      allAnomalies.forEach(a => {
        if (a.boundary_id) {
          if (!anomalyMap.has(a.boundary_id)) anomalyMap.set(a.boundary_id, []);
          anomalyMap.get(a.boundary_id)!.push(a);
        }
      });

      // Imagery via shared cache — batched + inter-batch delay
      const imageryMap = await getBatchedImagery(fields, 10);

      const entries: PrescriptionEntry[] = fields.map(field => {
        const records: CachedImageryRecord[] = imageryMap.get(field.id) ?? [];
        let latestIndices: LatestIndices = {};
        if (records.length > 0) {
          const latest = records[records.length - 1];
          const map: Record<string, number> = {};
          (latest.indices ?? []).forEach((i: any) => { map[i.index_type] = i.mean_value; });
          latestIndices = {
            NDVI: map['NDVI'],
            NDRE: map['NDRE'],
            NDWI: map['NDWI'],
            LST:  map['LST'],
            acquisitionDate: latest.acquisition_date?.slice(0, 10),
          };
        }
        const fieldAnomalies = anomalyMap.get(field.id) ?? [];
        const activeAnomalies = fieldAnomalies.filter(
          a => a.status !== 'resolved' && a.status !== 'false_positive'
        );
        const ndvi = latestIndices.NDVI ?? null;
        const zones = deriveZones(field, ndvi, field.area_hectares ?? 1);
        const { product, unit } = getProductDefaults(field.metadata?.crop_type);
        return { field, latestIndices, activeAnomalies, zones, recommendedProduct: product, unit, expanded: false };
      });

      setPrescriptions(entries);
    } catch (err: any) {
      setError(err.response?.data?.message || err.message || 'Failed to load field data');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { load(); }, [load]);

  const toggle = (idx: number) =>
    setPrescriptions(prev => prev.map((e, i) => i === idx ? { ...e, expanded: !e.expanded } : e));

  const handleRateChange = (entryIdx: number, zoneId: string, rate: number) =>
    setPrescriptions(prev => prev.map((e, i) =>
      i !== entryIdx ? e : {
        ...e,
        zones: e.zones.map(z => z.zoneId === zoneId ? { ...z, applicationRate: rate } : z),
      }
    ));

  const exportCSV = (entry: PrescriptionEntry) =>
    downloadText(buildCsv(entry), `vra_${entry.field.name.replace(/\s+/g, '_')}.csv`);

  const exportGeoJSON = (entry: PrescriptionEntry) =>
    downloadText(buildGeoJSON(entry), `vra_${entry.field.name.replace(/\s+/g, '_')}.geojson`);

  const totalFields = prescriptions.length;
  const stressedFields = prescriptions.filter(e => (e.latestIndices.NDVI ?? 1) < 0.45).length;
  const totalProduct = prescriptions.reduce(
    (s, e) => s + e.zones.reduce((zs, z) => zs + z.applicationRate * z.areaHa, 0), 0
  );

  return (
    <div className="space-y-6">

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h1 className="text-3xl font-bold text-gray-900 dark:text-white flex items-center gap-2">
            <Layers className="w-7 h-7 text-violet-600" /> Variable-Rate Scripting
          </h1>
          <p className="mt-1 text-sm text-gray-500 dark:text-gray-400">
            AI-derived application-rate prescriptions per field zone — export ISOXML CSV or GeoJSON
          </p>
        </div>
        <button type="button" onClick={() => load(true)} disabled={loading}
          className="flex items-center gap-1.5 text-xs text-gray-500 hover:text-gray-700 dark:text-gray-400 dark:hover:text-gray-200">
          <RefreshCw className={`w-3.5 h-3.5 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {/* ── Summary cards ──────────────────────────────────────────────────── */}
      {!loading && prescriptions.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          {[
            { label: 'Total Fields',          value: totalFields,                 colour: 'text-violet-600' },
            { label: 'Fields in Stress',       value: stressedFields,             colour: 'text-red-600' },
            { label: 'Fields Healthy',         value: totalFields - stressedFields, colour: 'text-green-600' },
            { label: `Est. Product (${prescriptions[0]?.unit ?? 'kg'})`, value: totalProduct.toFixed(0), colour: 'text-gray-700 dark:text-gray-300' },
          ].map(({ label, value, colour }) => (
            <Card key={label} className="p-4 text-center">
              <p className={`text-2xl font-bold ${colour}`}>{value}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{label}</p>
            </Card>
          ))}
        </div>
      )}

      {/* ── How VRA works ──────────────────────────────────────────────────── */}
      <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
        {[
          { icon: Activity,    colour: 'text-violet-500', label: 'NDVI Zone Derivation', desc: 'Satellite NDVI divides each field into high-stress, moderate, and healthy zones. Each zone gets its own application rate.' },
          { icon: Droplets,    colour: 'text-cyan-500',   label: 'Crop-Matched Product', desc: 'Product and unit defaults are matched to the crop type defined in Step 1 field metadata. Override per field as needed.' },
          { icon: FlaskConical,colour: 'text-green-500',  label: 'ISOXML Export',         desc: 'Export zone polygons and rates as an ISOXML-compatible CSV or GeoJSON file for precision agriculture machinery.' },
        ].map(({ icon: Icon, colour, label, desc }) => (
          <div key={label} className="rounded-lg bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 p-4 flex gap-3">
            <Icon className={`w-5 h-5 ${colour} flex-shrink-0 mt-0.5`} />
            <div>
              <p className="text-sm font-semibold text-gray-700 dark:text-gray-300">{label}</p>
              <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 leading-relaxed">{desc}</p>
            </div>
          </div>
        ))}
      </div>

      {/* ── Error / list ───────────────────────────────────────────────────── */}
      {error && <Alert type="error">{error}</Alert>}

      {loading ? (
        <div className="flex justify-center py-16"><Spinner size="lg" /></div>
      ) : prescriptions.length === 0 ? (
        <Card className="p-10 text-center">
          <ZapOff className="mx-auto w-12 h-12 text-gray-300 dark:text-gray-600 mb-3" />
          <p className="text-sm font-medium text-gray-500 dark:text-gray-400">No fields found</p>
          <p className="text-xs text-gray-400 mt-1">
            Define fields in AgroTech → Field Monitoring → Fields before generating VRA prescriptions.
          </p>
        </Card>
      ) : (
        <div className="space-y-4">
          {prescriptions.map((entry, idx) => (
            <PrescriptionCard
              key={entry.field.id}
              entry={entry}
              onToggle={() => toggle(idx)}
              onRateChange={(zoneId, rate) => handleRateChange(idx, zoneId, rate)}
              onExportCSV={() => exportCSV(entry)}
              onExportGeoJSON={() => exportGeoJSON(entry)}
            />
          ))}
        </div>
      )}
    </div>
  );
};

export default VariableRateScriptingPage;
// Made with Bob
